import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

/** Shared control look (inputs, select triggers, textareas, combobox triggers). */
export const controlVariants = cva(
  "w-full min-w-0 rounded-control border border-control bg-input text-fg font-medium shadow-xs outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-fg-subtle placeholder:font-normal hover:border-control-hover focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/20 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-control aria-invalid:border-danger-ink aria-invalid:focus-visible:ring-danger/20 read-only:bg-surface-2/50",
  {
    variants: {
      size: {
        sm: "h-8 px-2.5 text-sm rounded-[10px]",
        md: "h-9 px-3 text-base",
        lg: "h-11 px-3.5 text-md",
      },
    },
    defaultVariants: { size: "md" },
  },
);

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size">, VariantProps<typeof controlVariants> {
  /** Element inside the left edge (icon, currency symbol). */
  leading?: ReactNode;
  /** Element inside the right edge (unit, clear button, spinner). */
  trailing?: ReactNode;
  invalid?: boolean;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, containerClassName, size, leading, trailing, invalid, type = "text", ...props },
  ref,
) {
  const input = (
    <input
      ref={ref}
      type={type}
      aria-invalid={invalid || props["aria-invalid"] || undefined}
      className={cn(controlVariants({ size }), leading ? "pl-9" : undefined, trailing ? "pr-9" : undefined, className)}
      {...props}
    />
  );
  if (!leading && !trailing) return input;
  return (
    <div className={cn("relative flex w-full items-center", containerClassName)}>
      {leading && (
        <span className="pointer-events-none absolute left-3 flex items-center text-fg-subtle [&_svg]:size-4">{leading}</span>
      )}
      {input}
      {trailing && <span className="absolute right-2.5 flex items-center text-fg-subtle [&_svg]:size-4">{trailing}</span>}
    </div>
  );
});
